import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  AlertTriangle, BarChart3, Search, Users, Eye, MousePointerClick, TrendingUp, Loader2, RefreshCw,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { getReports } from "@/lib/reports.functions";
import { ReportHistory } from "@/components/dashboard/ReportHistory";



export const Route = createFileRoute("/_authenticated/dashboard/admin/reports")({
  component: ReportsPage,
});

const SITE_URL = "https://www.homesteadconstruction.us";




function Stat({ label, value, icon: Icon }: { label: string; value: string | number; icon: React.ComponentType<{ size?: number; className?: string }> }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon size={14} /> {label}</div>
      <p className="font-display text-2xl font-bold mt-1 text-ink">{value}</p>
    </div>
  );
}
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
const secs = (n: number) => `${Math.round(n)}s`;

function HealthBadge({ label, ok, note }: { label: string; ok: boolean; note?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${ok ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" : "bg-yellow-500/10 border-yellow-500/30 text-yellow-700"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-yellow-500"}`} />
      {label}: {ok ? "connected" : "issue"}
      {note && ok && <span className="text-muted-foreground">· {note}</span>}
    </span>
  );
}
function formatUpdated(ts: number | undefined) {
  if (!ts) return "—";
  const d = new Date(ts);
  const diff = Math.round((Date.now() - ts) / 1000);
  const rel = diff < 60 ? `${diff}s ago` : diff < 3600 ? `${Math.round(diff / 60)}m ago` : `${Math.round(diff / 3600)}h ago`;
  return `${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · ${rel}`;
}

function ReportsPage() {
  const [tab, setTab] = useState<"live" | "history">("live");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">Reports</h1>
        <p className="text-xs text-muted-foreground mt-1">Live analytics and the historical monthly report archive.</p>
      </div>
      <div className="inline-flex rounded-lg border border-border bg-card p-1 text-sm">
        <button onClick={() => setTab("live")} className={`px-3 py-1.5 rounded-md ${tab === "live" ? "bg-brand text-primary-foreground" : "text-muted-foreground"}`}>
          Live data
        </button>
        <button onClick={() => setTab("history")} className={`px-3 py-1.5 rounded-md ${tab === "history" ? "bg-brand text-primary-foreground" : "text-muted-foreground"}`}>
          Report history
        </button>
      </div>
      {tab === "live" ? <LiveDataPanel /> : <ReportHistory />}
    </div>
  );

}

function LiveDataPanel() {
  const [days, setDays] = useState(30);
  const fetchReports = useServerFn(getReports);
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["admin-reports", days],
    queryFn: () => fetchReports({ data: { days, siteUrl: SITE_URL } }),
    staleTime: 5 * 60_000,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 items-center">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-sm ${days === d ? "bg-brand text-primary-foreground" : "bg-card border border-border"}`}>
              {d} days
            </button>
          ))}
          <button onClick={() => refetch()} disabled={isFetching}
            className="px-3 py-1.5 rounded-lg text-sm bg-card border border-border inline-flex items-center gap-1.5 disabled:opacity-60">
            <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <HealthBadge label="GA4" ok={!data.errors.ga4} />
          <HealthBadge label="Search Console" ok={!data.errors.gsc} />
          <HealthBadge label="Realtime" ok={!data.errors.ga4} note={data.ga4 ? `${data.ga4.realtimeUsers} live` : undefined} />
          <span className="ml-auto text-muted-foreground">Last updated: {formatUpdated(dataUpdatedAt)}</span>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive rounded-lg p-4 text-sm">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle size={16} /> Failed to load reports</p>
          <p className="text-xs mt-1 break-words">{(error as Error).message}</p>
          <button onClick={() => refetch()} className="mt-2 text-xs underline">Retry</button>
        </div>
      )}
      {isLoading && (
        <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Loading reports…
        </div>
      )}

      <section className="space-y-3">
        <h2 className="font-display text-lg font-semibold text-ink flex items-center gap-2"><BarChart3 size={18} /> Google Analytics 4</h2>
        {data?.errors.ga4 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-xs">
            <p className="font-semibold text-yellow-700 flex items-center gap-2"><AlertTriangle size={14} /> GA4 issue</p>
            <p className="mt-1 break-words text-foreground">{data.errors.ga4}</p>
          </div>
        )}
        {data?.ga4 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <Stat label="Sessions" value={data.ga4.summary.sessions.toLocaleString()} icon={MousePointerClick} />
              <Stat label="Users" value={data.ga4.summary.users.toLocaleString()} icon={Users} />
              <Stat label="Pageviews" value={data.ga4.summary.pageviews.toLocaleString()} icon={Eye} />
              <Stat label="Bounce rate" value={pct(data.ga4.summary.bounceRate)} icon={TrendingUp} />
              <Stat label="Avg session" value={secs(data.ga4.summary.avgDuration)} icon={TrendingUp} />
              <Stat label="Live now" value={data.ga4.realtimeUsers} icon={Users} />
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="font-display font-semibold mb-3">Sessions over time</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.ga4.byDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="date" stroke="var(--color-muted-foreground)" fontSize={11} />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={11} />
                    <Tooltip />
                    <Line type="monotone" dataKey="sessions" stroke="var(--color-brand)" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="pageviews" stroke="var(--color-muted-foreground)" strokeWidth={1} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MiniTable title="Top pages" rows={data.ga4.topPages.map((p) => [p.path, p.views.toLocaleString()])} />
              <MiniTable title="Top referrers" rows={data.ga4.referrers.map((r) => [r.source, r.sessions.toLocaleString()])} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MiniTable title="Devices" rows={data.ga4.devices.map((d) => [d.device, d.sessions.toLocaleString()])} />
              <MiniTable title="Top countries" rows={data.ga4.countries.map((c) => [c.country, c.sessions.toLocaleString()])} />
            </div>
          </>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-lg font-semibold text-ink flex items-center gap-2"><Search size={18} /> Google Search Console</h2>
        {data?.errors.gsc && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-xs">
            <p className="font-semibold text-yellow-700 flex items-center gap-2"><AlertTriangle size={14} /> Search Console issue</p>
            <p className="mt-1 break-words text-foreground">{data.errors.gsc}</p>
          </div>
        )}
        {data?.gsc && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Clicks" value={data.gsc.totals.clicks.toLocaleString()} icon={MousePointerClick} />
              <Stat label="Impressions" value={data.gsc.totals.impressions.toLocaleString()} icon={Eye} />
              <Stat label="CTR" value={pct(data.gsc.totals.ctr)} icon={TrendingUp} />
              <Stat label="Avg position" value={data.gsc.totals.position.toFixed(1)} icon={TrendingUp} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <MiniTable title="Top queries" rows={data.gsc.topQueries.map((q) => [q.query, q.clicks.toLocaleString()])} />
              <MiniTable title="Top pages" rows={data.gsc.topPages.map((p) => [p.page, p.clicks.toLocaleString()])} />
            </div>
          </>
        )}

      </section>
    </div>
  );
}

function MiniTable({ title, rows }: { title: string; rows: (string | number)[][] }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <h3 className="font-display font-semibold mb-3">{title}</h3>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([a, b], i) => (
            <tr key={i} className="border-b border-border/50 last:border-0">
              <td className="py-2 truncate max-w-[260px]">{a}</td>
              <td className="py-2 text-right font-semibold">{b}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td className="py-3 text-muted-foreground text-center">No data.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
